import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PackageSearch } from "lucide-react";
import { ProductCard } from "@/components/Featured";
import Reveal from "@/components/Reveal";
import api from "@/lib/api";

const FILTERS = [
  ["all", "All boxes"],
  ["gift", "Gift"],
  ["pizza", "Pizza"],
  ["cake", "Cake"],
  ["burger", "Burger"],
  ["chalk", "Chalk"],
  ["juice", "Juice"],
  ["takeaway", "Takeaway"],
  ["bags", "Bags"],
  ["packaging", "Packaging"],
  ["corrugated", "Corrugated"],
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [params, setParams] = useSearchParams();
  const cat = params.get("category") || "all";

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const shown = useMemo(
    () => (cat === "all" ? products : products.filter((p) => p.category === cat)),
    [products, cat]
  );

  const pick = (slug) => {
    if (slug === "all") params.delete("category");
    else params.set("category", slug);
    setParams(params, { replace: true });
  };

  return (
    <main className="pt-32 pb-24 min-h-screen" data-testid="shop-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-mintsoft/90 mb-4">
            The Shop — Kampala
          </p>
          <h1 className="font-display font-semibold tracking-tight text-4xl sm:text-6xl text-kraft mb-4">
            Every box, made to size
          </h1>
          <p className="max-w-xl text-kraft/60 mb-10">
            Browse the range and add styles to your quote cart. No listed prices — every
            order is quoted on size, print and volume.
          </p>
        </Reveal>

        <div className="flex flex-wrap gap-2 mb-12" data-testid="shop-filters">
          {FILTERS.map(([slug, label]) => (
            <button
              key={slug}
              data-testid={`shop-filter-${slug}`}
              onClick={() => pick(slug)}
              className={`rounded-full px-5 h-10 font-mono text-[11px] tracking-[0.15em] uppercase border transition-colors ${
                cat === slug
                  ? "bg-mint text-forest border-mint font-semibold"
                  : "border-mint/25 text-kraft/70 hover:border-mint hover:text-mint"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {shown.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 py-24 text-center" data-testid="shop-empty-state">
            <PackageSearch size={40} className="text-mintsoft/50" />
            <p className="font-display text-xl text-kraft">No boxes here yet</p>
            <p className="text-sm text-kraft/55">Try another family from the filters above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {shown.map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 0.06} className="h-full">
                <ProductCard p={p} index={i} />
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
